import { z } from 'zod';

/**
 * Data of the PV prequalification sheet, validated before rendering. Values are neutral codes and numbers;
 * labels, units and formats come from the translation catalogs. Free texts are short summaries written by the
 * extraction in the sheet's language: their maximum lengths keep the sheet on one page.
 */

/** Explicit "to be determined": the documents do not give the value yet. Different from an empty field. */
export const toBeDetermined = z.literal('tbd');
export type ToBeDetermined = z.infer<typeof toBeDetermined>;

const text = (max: number) => z.string().trim().min(1).max(max);
const isoDate = z.iso.date();
const isoDateTime = z.iso.datetime({ offset: true });
const percent = z.number().min(0).max(100);

/** Civil dates remain dates: no appointment time is inferred. Legacy timestamp visits remain readable. */
const visitDate = z.union([isoDate, isoDateTime]);
const visitContact = z
    .strictObject({
        name: text(80).nullable().default(null),
        phone: text(50).nullable().default(null),
        email: z.email().max(254).nullable().default(null),
    })
    .refine((contact) => contact.name !== null || contact.phone !== null || contact.email !== null, {
        message: 'Empty visit contact',
    });
const visitContacts = z.array(visitContact).max(3).default([]);
const visitDetails = {
    mandatory: z.boolean(),
    contacts: visitContacts,
    bookingNoticeHours: z.number().int().positive().max(8760).nullable().default(null),
    detail: text(90).nullable().default(null),
};
export const siteVisit = z.union([
    z.strictObject({ at: isoDateTime, mandatory: z.boolean(), contacts: z.array(visitContact).max(3).optional() }),
    z.strictObject({ kind: z.literal('fixed'), at: visitDate, ...visitDetails }),
    z
        .strictObject({ kind: z.literal('multiple'), dates: z.array(visitDate).min(2).max(40), ...visitDetails })
        .refine((visit) => new Set(visit.dates.map((date) => Date.parse(date))).size === visit.dates.length, {
            path: ['dates'],
            message: 'Duplicate visit dates',
        }),
    z.strictObject({ kind: z.literal('appointment'), until: visitDate.nullable(), ...visitDetails }),
]);

export const roofType = z.enum([
    'flat',
    'steelDeck',
    'carport',
    'ground',
    'slate',
    'tiles',
    'shingles',
    'fiberCement',
    'zinc',
    'other',
]);
export type RoofType = z.infer<typeof roofType>;

export const buyerType = z.enum([
    'municipality',
    'intermunicipality',
    'department',
    'region',
    'state',
    'healthcare',
    'education',
    'socialHousing',
    'publicCompany',
    'private',
    'other',
]);

export const procedureType = z.enum([
    'adapted',
    'openTender',
    'restrictedTender',
    'negotiated',
    'competitiveDialogue',
    'other',
]);

/**
 * Context tags chosen from the documents. Mandatory site visit, imposed brand and allotment are derived from
 * other fields (`sheetTags`), so they cannot contradict them.
 */
export const siteTag = z.enum([
    'newBuild',
    'renovation',
    'occupiedSite',
    'publicAccessBuilding',
    'classifiedFacility',
    'variantsAllowed',
]);
export type SiteTag = z.infer<typeof siteTag>;
export const derivedTag = z.enum(['mandatorySiteVisit', 'imposedBrand', 'allotment', 'singleLot']);
export type SheetTag = SiteTag | z.infer<typeof derivedTag>;

export const equipmentKind = z.enum([
    // Prescribed inverter, with no documented technology; never inferred from its presence alone.
    'inverter',
    'stringInverter',
    'microinverter',
    'optimizer',
    'dcBox',
    'acBox',
    'emergencyStop',
]);
export type EquipmentKind = z.infer<typeof equipmentKind>;
export const powerUnit = z.enum(['W', 'kW', 'VA', 'kVA', 'A']);

const equipmentReference = {
    brand: text(24).nullable(),
    model: text(30).nullable(),
    /** Explicit contractor choice under the specifications; absence of a reference alone is not proof. */
    referenceSelection: z.enum(['contractorChoice', 'unspecified']).default('unspecified'),
    imposed: z.boolean().default(false),
};
const quantityValue = z.number().int().positive().max(100_000);
const equipmentQuantity = z.union([
    quantityValue,
    toBeDetermined,
    // One population with mutually exclusive counts, never cumulative equipment rows.
    z.strictObject({
        alternatives: z
            .array(quantityValue)
            .min(2)
            .max(3)
            .refine((values) => new Set(values).size === values.length, { message: 'Duplicate quantity alternatives' }),
    }),
]);

/** One conversion/protection row. Only emergency stops have a cutoff scope and no applicable power. */
export const equipment = z
    .object({
        kind: equipmentKind,
        ...equipmentReference,
        power: z.union([
            z.object({
                value: z.number().positive().max(1_000_000),
                unit: powerUnit,
                /** Source-supported rating or sizing bound; absence preserves the original representation. */
                qualifier: z.enum(['minimum', 'maximum']).optional(),
                approximate: z.boolean().optional(),
            }),
            toBeDetermined,
            z.literal('notApplicable'),
        ]),
        quantity: equipmentQuantity,
        // No default: an unknown scope is not evidence of AC-only cutoff.
        cutoffScope: z.enum(['ac', 'acDc', 'unspecified']).optional(),
    })
    .refine(
        (line) => (line.kind === 'emergencyStop' ? line.cutoffScope !== undefined : line.cutoffScope === undefined),
        {
            path: ['cutoffScope'],
            message: 'Cutoff scope is required only for emergency stops',
        },
    )
    .refine((line) => (line.kind === 'emergencyStop') === (line.power === 'notApplicable'), {
        path: ['power'],
        message: 'Only emergency stops require non-applicable power',
    })
    .refine((line) => !line.imposed || line.brand !== null, {
        path: ['imposed'],
        message: 'An imposed equipment names its brand',
    })
    .refine(
        (line) =>
            line.referenceSelection !== 'contractorChoice' ||
            (!line.imposed && line.brand === null && line.model === null),
        {
            path: ['referenceSelection'],
            message: 'Contractor choice cannot name or impose a reference',
        },
    );
export type Equipment = z.infer<typeof equipment>;

export const awardCriterion = z.enum(['price', 'technicalValue', 'deadlines', 'environment', 'other']);

export const prerequisiteKind = z.enum(['qualification', 'insurance', 'references', 'turnover', 'workforce', 'other']);
export type PrerequisiteKind = z.infer<typeof prerequisiteKind>;

export const documentType = z.enum([
    'consultationRules',
    'administrativeSpecifications',
    'technicalSpecifications',
    'commitmentForm',
    /** Bid-writing guide listing the expected candidature and offer documents, often an RC annex. */
    'responseGuide',
    'other',
]);

/** Eligibility conditions and specific admissibility requirements, not descriptive declarations without thresholds. */
export const prerequisite = z.object({
    kind: prerequisiteKind,
    text: text(90),
    /** Where the requirement is written, when it matters to the reader (turnover in the RC, for example). */
    source: z
        .object({ document: documentType, article: text(12).nullable() })
        .nullable()
        .default(null),
});

export const maintenance = z.discriminatedUnion('status', [
    // Independent services: neither succession nor overlap is implied. No historical conversion.
    z.strictObject({
        status: z.literal('mixed'),
        included: z.strictObject({
            scope: text(90),
            durationMonths: z.number().int().positive().max(600).nullable(),
        }),
        optional: z.strictObject({
            scope: text(90),
            durationMonths: z.number().int().positive().max(600).nullable(),
            proposalRequired: z.boolean(),
        }),
    }),
    z.object({
        status: z.literal('included'),
        /** What the maintenance covers: one annual visit, remote monitoring, corrective work… */
        scope: text(90),
        durationMonths: z.number().int().positive().max(600).nullable(),
        /** Optional extension priced separately (PSE), if any. */
        optionalServiceMonths: z.number().int().positive().max(600).nullable().default(null),
    }),
    z.object({
        status: z.literal('optional'),
        scope: text(90),
        durationMonths: z.number().int().positive().max(600).nullable(),
        /** A mandatory proposal does not establish a firm order for the service. */
        proposalRequired: z.boolean(),
    }),
    z.object({ status: z.literal('notIncluded') }),
    z.object({ status: z.literal('unknown') }),
]);

export const moduleTechnology = z.enum(['monocrystalline', 'polycrystalline', 'thinFilm']);

/** Complementary trades the bidder must cover beyond the PV installation, kept in the order given. */
export const scopeTrade = z.enum([
    'design',
    'structuralEngineering',
    'steelStructure',
    'geotechnics',
    'worksSupervision',
    'civilWorks',
    'hvWorks',
    'electricalWorks',
    'roofing',
]);

/**
 * Wider obligations of an expanded scope: the list of complementary trades, or the two prose summaries
 * (`includedWorks`, `responseNote`) of runs prepared before it. Exactly one representation, never converted.
 */
const widerObligations = {
    additionalTrades: z
        .array(scopeTrade)
        .min(1)
        .max(6)
        .refine((trades) => new Set(trades).size === trades.length, { message: 'Duplicate trade' })
        .optional(),
    includedWorks: text(220).optional(),
    responseNote: text(240).optional(),
};
const oneRepresentation = (scope: { additionalTrades?: unknown; includedWorks?: string; responseNote?: string }) =>
    scope.additionalTrades === undefined
        ? scope.includedWorks !== undefined && scope.responseNote !== undefined
        : scope.includedWorks === undefined && scope.responseNote === undefined;
const representationMessage = { message: 'Expected additionalTrades or both legacy summaries' };

/** Current works contract/lot, not the enclosing operation. Absence means unreviewed historical scope. */
export const contractScope = z.discriminatedUnion('kind', [
    z.strictObject({ kind: z.literal('pvWorks') }),
    z
        .strictObject({ kind: z.literal('broaderWorks'), ...widerObligations })
        .refine(oneRepresentation, representationMessage),
    z
        .strictObject({
            kind: z.literal('pvInBroaderLot'),
            lotTitle: text(90),
            /** Minority status requires documentary evidence, never inference from the lot title. */
            pvShare: z.enum(['minority', 'notMinority', 'unknown']),
            ...widerObligations,
        })
        .refine(oneRepresentation, representationMessage),
]);

export const pvSheetTender = z
    .object({
        reference: text(30),
        title: text(150),
        // No default: parsing/retrying historical publications must not invent or inject a scope.
        contractScope: contractScope.optional(),
        // A retained lot identifier can exceed the number of lots in a partial relaunch.
        lot: z.object({ number: z.number().int().positive().nullable(), count: z.number().int().positive().max(99) }),
        procedure: procedureType,
        buyer: z.object({ name: text(70), type: buyerType }),
        location: z.object({
            city: text(45),
            postcode: text(10),
            departmentName: text(30),
            departmentCode: text(3),
            longitude: z.number().min(-180).max(180),
            latitude: z.number().min(-90).max(90),
        }),
        noticeUrl: z.url({ protocol: /^https?$/ }).max(300),
        dates: z.object({
            publishedOn: isoDate,
            questionsUntil: isoDateTime.nullable(),
            siteVisit: siteVisit.nullable(),
            submissionDeadline: isoDateTime,
            // No default: historical deadlines retain their original representation and generic label.
            submissionStage: z.enum(['applications', 'initialOffers', 'finalOffers']).optional(),
            works: z
                .object({
                    start: isoDate.nullable(),
                    end: isoDate.nullable(),
                    startMonth: z
                        .string()
                        .regex(/^\d{4}-(0[1-9]|1[0-2])$/)
                        .nullable()
                        .default(null),
                    durationWeeks: z.number().int().positive().max(520).nullable(),
                    durationDays: z.number().int().positive().max(3_650).nullable().default(null),
                    durationMonths: z.number().int().positive().max(120).nullable().optional(),
                    preparationMonths: z.number().int().positive().max(120).nullable().optional(),
                    // No defaults: historical publication payloads and hashes must remain unchanged.
                    preparationWeeks: z.number().int().positive().max(520).optional(),
                    durationIncludesPreparation: z.boolean().optional(),
                    durationIsMaximum: z.boolean().optional(),
                    provisional: z.boolean().default(false),
                })
                .refine((works) => works.start === null || works.end === null || works.start <= works.end, {
                    message: 'Works end before they start',
                })
                .refine(
                    (works) =>
                        works.startMonth === null || works.end === null || works.startMonth <= works.end.slice(0, 7),
                    {
                        message: 'Works end before their start month',
                    },
                )
                .refine(
                    (works) =>
                        works.startMonth === null || works.start === null || works.start.startsWith(works.startMonth),
                    {
                        message: 'Works start and month disagree',
                    },
                )
                .refine(
                    (works) =>
                        [works.durationDays, works.durationWeeks, works.durationMonths].filter(
                            (value) => value !== null && value !== undefined,
                        ).length <= 1,
                    {
                        message: 'Use one works duration unit',
                    },
                )
                .refine(
                    (works) =>
                        works.preparationWeeks === undefined ||
                        works.preparationMonths === null ||
                        works.preparationMonths === undefined,
                    { message: 'Use one preparation duration unit' },
                )
                .refine(
                    (works) =>
                        works.durationIncludesPreparation === undefined ||
                        [works.durationDays, works.durationWeeks, works.durationMonths].some(
                            (value) => value !== null && value !== undefined,
                        ),
                    { message: 'Duration scope requires a duration' },
                )
                .refine(
                    (works) =>
                        !works.durationIncludesPreparation ||
                        ((works.preparationWeeks === undefined ||
                            works.durationWeeks === null ||
                            works.preparationWeeks <= works.durationWeeks) &&
                            (works.preparationMonths === null ||
                                works.preparationMonths === undefined ||
                                works.durationMonths === null ||
                                works.durationMonths === undefined ||
                                works.preparationMonths <= works.durationMonths)),
                    { message: 'Included preparation exceeds total duration' },
                )
                .refine(
                    (works) =>
                        !works.durationIsMaximum ||
                        [works.durationDays, works.durationWeeks, works.durationMonths].some(
                            (value) => value !== null && value !== undefined,
                        ),
                    { message: 'Maximum requires a duration' },
                )
                .nullable(),
        }),
        size: z.object({
            moduleCount: z.union([z.number().int().positive().max(1_000_000), toBeDetermined]),
            peakPowerKw: z.union([z.number().positive().max(1_000_000), toBeDetermined]),
        }),
        tags: z
            .array(siteTag)
            .max(5)
            .refine((tags) => new Set(tags).size === tags.length, { message: 'Duplicate tag' })
            .refine((tags) => !(tags.includes('newBuild') && tags.includes('renovation')), {
                message: 'A site is either new or renovated',
            }),
        roof: z
            .object({ type: roofType, detail: text(80).nullable() })
            // An identified support outside the named categories, never a fallback for missing information.
            .refine((roof) => roof.type !== 'other' || roof.detail !== null, {
                path: ['detail'],
                message: 'Other roof requires a description',
            }),
        mounting: z
            .object({
                /** Null for an absent reference; legacy data implied contractor choice. */
                product: text(40).nullable(),
                // Explicit in new analyses, absent in historical outputs: never backfilled.
                referenceSelection: z.enum(['contractorChoice', 'unspecified']).optional(),
                imposed: z.boolean(),
                equivalentAllowed: z.boolean().nullable(),
                detail: text(80).nullable(),
            })
            .refine((mounting) => !mounting.imposed || mounting.product !== null, {
                path: ['imposed'],
                message: 'An imposed mounting names its product',
            })
            .refine(
                (mounting) =>
                    mounting.referenceSelection !== 'contractorChoice' ||
                    (!mounting.imposed && mounting.product === null),
                {
                    path: ['referenceSelection'],
                    message: 'Contractor choice cannot name or impose a mounting reference',
                },
            )
            .refine(
                (mounting) =>
                    mounting.equivalentAllowed !== null ||
                    mounting.product !== null ||
                    mounting.referenceSelection !== undefined,
                {
                    path: ['referenceSelection'],
                    message: 'Unknown equivalence without a reference requires an explicit selection status',
                },
            ),
        modules: z.object({
            /** Reference equipment recommended by the documents, not necessarily exclusive. */
            brand: text(40).nullable().default(null),
            model: text(60).nullable().default(null),
            /** Null means the documents do not establish whether an equivalent is allowed. */
            equivalentAllowed: z.boolean().nullable().default(null),
            minPowerW: z.number().positive().max(2_000).nullable(),
            technology: moduleTechnology.nullable(),
            /** kg CO₂ eq/kWc. */
            maxCarbonFootprint: z.number().positive().max(2_000).nullable(),
            detail: text(80).nullable(),
        }),
        // One additional row for a prescribed emergency stop; overflow still fails at render time.
        conversion: z.array(equipment).min(1).max(6),
        awardCriteria: z
            .array(z.object({ criterion: awardCriterion, weightPercent: percent }))
            .max(5)
            .refine(
                (criteria) =>
                    criteria.length === 0 ||
                    Math.abs(criteria.reduce((sum, item) => sum + item.weightPercent, 0) - 100) < 0.5,
                { message: 'Award criteria weights must total 100%' },
            ),
        prerequisites: z.array(prerequisite).max(7),
        maintenance,
    })
    .superRefine((tender, context) => {
        const { publishedOn, questionsUntil, siteVisit, submissionDeadline } = tender.dates;
        if (!isoDateTime.safeParse(submissionDeadline).success) return;
        const deadline = Date.parse(submissionDeadline);
        if (Date.parse(publishedOn) > deadline) {
            context.addIssue({ code: 'custom', path: ['dates', 'publishedOn'], message: 'Published after deadline' });
        }
        for (const [field, value] of [['questionsUntil', questionsUntil]] as const) {
            if (value !== null && Date.parse(value) > deadline) {
                context.addIssue({ code: 'custom', path: ['dates', field], message: 'Later than the deadline' });
            }
        }
        const visitDates =
            siteVisit === null
                ? []
                : !('kind' in siteVisit) || siteVisit.kind === 'fixed'
                  ? [siteVisit.at]
                  : siteVisit.kind === 'multiple'
                    ? siteVisit.dates
                    : siteVisit.until
                      ? [siteVisit.until]
                      : [];
        // No worksite time zone is recorded. Reject civil dates only when later in every zone (UTC+14 bound).
        // Borderline civil dates require source review, not a fabricated hour or the timestamp’s written offset.
        for (const date of visitDates) {
            const tooLate =
                date.length === 10
                    ? date > new Date(deadline + 14 * 60 * 60 * 1000).toISOString().slice(0, 10)
                    : Date.parse(date) > deadline;
            if (tooLate)
                context.addIssue({
                    code: 'custom',
                    path: ['dates', 'siteVisit'],
                    message: 'Visit later than the submission deadline',
                });
        }
    });
export type PvSheetTender = z.infer<typeof pvSheetTender>;

/** Tags shown in the sidebar: chosen tags first, then those derived from the other fields. */
export function sheetTags(tender: PvSheetTender): SheetTag[] {
    const tags: SheetTag[] = [...tender.tags];
    if (tender.dates.siteVisit?.mandatory) {
        tags.push('mandatorySiteVisit');
    }
    if (
        tender.conversion.some((line) => line.imposed) ||
        tender.mounting.imposed ||
        (tender.modules.brand !== null && tender.modules.equivalentAllowed === false)
    ) {
        tags.push('imposedBrand');
    }
    if (tender.lot.count > 1) {
        tags.push('allotment');
    } else if (tender.lot.count === 1) {
        tags.push('singleLot');
    }
    return tags;
}

/** Prerequisites always listed on the sheet, even when the analysed documents say nothing about them. */
export const expectedPrerequisites: readonly PrerequisiteKind[] = ['qualification', 'insurance'];
